import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { graphql } from '@/gql';
import type {
  TicketSalesInterval,
  TicketSalesNightsQuery,
  TicketSalesNightsQueryVariables,
} from '@/gql/graphql';
import { Search, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { RangeDatePicker, type RangeValue } from '@/components/ui/DatePickers';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { useDebounced } from '@/hooks/useDebounced';
import { formatDate } from '@/lib/format';
import { useTheme } from '@/layout/useTheme';
import { seriesColors } from './chartPalette';
import { escapeChartText } from './chartText';
import { thinnedLabelFormatter } from './ticketSalesAxis';
import { bucketLabel, drawnSeries, TicketSalesSeriesDoc } from './ticketSalesSeries';

const amsterdamDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The venue-clock calendar day (YYYY-MM-DD) of an instant, whatever the browser zone is. */
function amsterdamDay(iso: string): string {
  return amsterdamDayFormat.format(new Date(iso));
}

const TicketSalesNightsDoc = graphql(/* GraphQL */ `
  query TicketSalesNights {
    ticketSalesNights {
      nightId
      name
      startsAt
      upcoming
    }
  }
`) as TypedDocumentNode<TicketSalesNightsQuery, TicketSalesNightsQueryVariables>;

export type ChartKind = 'BAR' | 'LINE';

const INTERVAL_OPTIONS: Array<{ value: TicketSalesInterval; label: string }> = [
  { value: 'DAY', label: 'Day' },
  { value: 'HOUR', label: 'Hour' },
  { value: 'WEEK', label: 'Week' },
];

const KIND_OPTIONS: Array<{ value: ChartKind; label: string }> = [
  { value: 'BAR', label: 'Bar' },
  { value: 'LINE', label: 'Line' },
];

/**
 * The Grouping and Chart type selects and the Cumulative / Split by stage switches of the Ticket
 * sales preset. The Charts page renders them in its header control group, next to the Preset
 * select, where the other presets show date and bucket.
 */
export function TicketSalesControls({
  interval,
  kind,
  cumulative,
  splitByStage,
  onIntervalChange,
  onKindChange,
  onCumulativeChange,
  onSplitByStageChange,
}: {
  interval: TicketSalesInterval;
  kind: ChartKind;
  cumulative: boolean;
  splitByStage: boolean;
  onIntervalChange: (next: TicketSalesInterval) => void;
  onKindChange: (next: ChartKind) => void;
  onCumulativeChange: (next: boolean) => void;
  onSplitByStageChange: (next: boolean) => void;
}) {
  return (
    <>
      <Select
        id="admin.charts.ticketSales.interval"
        value={interval}
        onChange={(event) => onIntervalChange(event.target.value as TicketSalesInterval)}
        options={INTERVAL_OPTIONS}
        aria-label="Grouping"
      />
      <Select
        id="admin.charts.ticketSales.type"
        value={kind}
        onChange={(event) => onKindChange(event.target.value as ChartKind)}
        options={KIND_OPTIONS}
        aria-label="Chart type"
      />
      <div className="flex h-10 items-center">
        <Switch
          id="admin.charts.ticketSales.cumulative"
          label="Cumulative"
          checked={cumulative}
          onChange={(event) => onCumulativeChange(event.target.checked)}
        />
      </div>
      <div className="flex h-10 items-center">
        <Switch
          id="admin.charts.ticketSales.splitByStage"
          label="Split by stage"
          checked={splitByStage}
          onChange={(event) => onSplitByStageChange(event.target.checked)}
        />
      </div>
    </>
  );
}

/**
 * Charts page, "Ticket sales" preset: counted tickets per bucket before doors close, for the
 * nights ticked in the picker, drawn on one shared "N before" axis so nights compare.
 * One series per night (stages summed), or per night and sale stage with Split by
 * stage. The ticked nights live in the page (the URL's `nights`): null is the default
 * selection (the upcoming nights, none when none is upcoming) until the user ticks something.
 */
export function TicketSalesChart({
  interval,
  kind,
  cumulative,
  splitByStage,
  nightIds: pickedIds,
  onNightsChange,
}: {
  interval: TicketSalesInterval;
  kind: ChartKind;
  cumulative: boolean;
  splitByStage: boolean;
  nightIds: string[] | null;
  /**
   * Change the ticked list from its current value (null while it is still the default), so two
   * quick ticks both land. The result is the full ticked list in picker order; an empty list is
   * an explicit "none".
   */
  onNightsChange: (update: (current: string[] | null) => string[]) => void;
}) {
  const { resolved: theme } = useTheme();
  // The picker's finder: a title search (debounced) and an optional start-date range, both narrowing the list only.
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<RangeValue>({ from: '', to: '' });
  const debouncedSearch = useDebounced(search.trim().toLowerCase(), 250);

  const {
    data: nightsData,
    loading: nightsLoading,
    error: nightsError,
  } = useQuery(TicketSalesNightsDoc);
  const nights = useMemo(
    () => nightsData?.ticketSalesNights ?? [],
    [nightsData],
  );
  const defaultIds = useMemo(
    () => nights.filter((p) => p.upcoming).map((p) => p.nightId),
    [nights],
  );
  const selected = useMemo<ReadonlySet<string>>(
    () => new Set(pickedIds ?? defaultIds),
    [pickedIds, defaultIds],
  );
  // The finder never changes the selection: ticked nights outside the filter stay ticked and stay in the chips.
  const visibleNights = useMemo(
    () =>
      nights.filter((p) => {
        if (debouncedSearch && !p.name.toLowerCase().includes(debouncedSearch)) return false;
        const day = amsterdamDay(p.startsAt);
        if (range.from && day < range.from) return false;
        if (range.to && day > range.to) return false;
        return true;
      }),
    [nights, debouncedSearch, range],
  );
  const chips = useMemo(() => nights.filter((p) => selected.has(p.nightId)), [nights, selected]);
  const nightIds = useMemo(() => chips.map((p) => p.nightId), [chips]);

  const {
    data,
    previousData,
    loading: seriesLoading,
    error: seriesError,
  } = useQuery(TicketSalesSeriesDoc, {
    skip: nightIds.length === 0,
    variables: { nightIds, interval },
  });
  const payload =
    nightIds.length === 0 ? undefined : (data ?? previousData)?.ticketSalesSeries;

  const toggle = (nightId: string, on: boolean) => {
    onNightsChange((current) => {
      const next = new Set(current ?? defaultIds);
      if (on) next.add(nightId);
      else next.delete(nightId);
      return nights.filter((p) => next.has(p.nightId)).map((p) => p.nightId);
    });
  };

  const categories = useMemo(
    () => (payload?.categories ?? []).map((n) => bucketLabel(n, interval)),
    [payload, interval],
  );
  // Bars stack the stages of one night into one bar only when split; a line never stacks.
  const stacked = kind === 'BAR' && splitByStage;
  const series = useMemo(() => {
    const rows = payload?.series ?? [];
    const startsAt = new Map(nights.map((p) => [p.nightId, p.startsAt]));
    // Two selected nights with one title (a recurring night) are told apart by their date.
    const titleCount = new Map<string, number>();
    for (const id of new Set(rows.map((s) => s.nightId))) {
      const name = rows.find((s) => s.nightId === id)?.nightName ?? '';
      titleCount.set(name, (titleCount.get(name) ?? 0) + 1);
    }
    const label = (nightId: string, name: string) => {
      const at = startsAt.get(nightId);
      return (titleCount.get(name) ?? 0) > 1 && at ? `${name} · ${formatDate(at)}` : name;
    };

    let shaped: Array<{ nightId: string; name: string; data: number[]; pending: number }>;
    if (splitByStage) {
      shaped = rows.map((s) => ({
        nightId: s.nightId,
        name: `${label(s.nightId, s.nightName)} · ${s.stageName}`,
        data: [...s.data],
        pending: s.pending,
      }));
    } else {
      const byNight = new Map<
        string,
        { nightId: string; name: string; data: number[]; pending: number }
      >();
      for (const s of rows) {
        const sum = byNight.get(s.nightId) ?? {
          nightId: s.nightId,
          name: label(s.nightId, s.nightName),
          data: s.data.map(() => 0),
          // All stage series of a night carry the same pending: the first one's stands.
          pending: s.pending,
        };
        s.data.forEach((v, i) => {
          sum.data[i] = (sum.data[i] ?? 0) + v;
        });
        byNight.set(s.nightId, sum);
      }
      shaped = [...byNight.values()];
    }
    const seen = new Set<string>();
    return shaped.map((s) => {
      // Apex renders series names as HTML: escape the night and stage names. A stacked
      // group finds a segment with indexOf(name), so equal names get an invisible word joiner.
      let name = escapeChartText(s.name);
      while (seen.has(name)) name += '\u2060';
      seen.add(name);
      // Up to today: null draws nothing in the buckets still to come (see drawnSeries).
      const data = drawnSeries(s.data, s.pending, cumulative);
      return {
        name,
        ...(stacked ? { group: s.nightId } : {}),
        data,
      };
    });
  }, [payload, nights, splitByStage, cumulative, stacked]);

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: kind === 'BAR' ? 'bar' : 'line',
        stacked,
        toolbar: { show: false },
        fontFamily: 'inherit',
        animations: { enabled: false },
        height: 400,
      },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '70%' } },
      stroke: { width: kind === 'BAR' ? 0 : 2, curve: 'straight' },
      markers: { size: kind === 'BAR' ? 0 : 3 },
      colors: seriesColors(series.length),
      xaxis: {
        categories,
        // A line's first point sits on the y axis; centre the points in their slots, like bars, so
        // the first (tilted) label is not clipped on the left.
        tickPlacement: kind === 'BAR' ? 'on' : 'between',
        // Long spans (hours) draw only every step-th label, counted from the last bucket, so "Doors
        // close" always shows (see thinnedLabelFormatter). Hour labels are long: tilt them so none
        // is clipped.
        labels: {
          hideOverlappingLabels: false,
          trim: false,
          formatter: thinnedLabelFormatter(categories),
          ...(interval === 'HOUR' ? { rotate: -45, rotateAlways: true } : {}),
        },
      },
      // The first tilted hour label is long: leave room so it is not clipped on the left.
      grid: interval === 'HOUR' ? { padding: { left: 60 } } : {},
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: { formatter: (v: number) => String(Math.round(v)) },
      },
      // A single series still names its night (and stage).
      legend: { position: 'top', showForSingleSeries: true },
      dataLabels: { enabled: false },
      tooltip: {
        theme: theme === 'dark' ? 'dark' : 'light',
        y: {
          formatter: (v: number | null) =>
            v === null ? '' : `${v} ${v === 1 ? 'ticket' : 'tickets'}`,
        },
      },
    }),
    [kind, stacked, interval, categories, series.length, theme],
  );
  const chartKey = [
    kind,
    interval,
    cumulative,
    splitByStage,
    categories.length,
    categories[0],
    series.map((s) => s.name).join('|'),
    // A bucket turning from pending to drawn (an hour passing) must not leave a stale null behind.
    (payload?.series ?? []).map((s) => s.pending).join(','),
  ].join('::');

  const loading = nightsLoading || (seriesLoading && !payload);
  const error = nightsError ?? seriesError;

  return (
    <div className="space-y-4">
      <Card
        title="Nights"
        subtitle="Newest first. Upcoming nights are ticked until you choose. Search or pick a date range to narrow the list."
      >
        <div className="space-y-3 px-5 py-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              id="admin.charts.ticketSales.search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search nights"
              aria-label="Search nights"
              leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
            />
            <div id="admin.charts.ticketSales.range">
              <RangeDatePicker
                value={range}
                onChange={setRange}
                onClear={() => setRange({ from: '', to: '' })}
                placeholder="Any date"
                ariaLabel="Night date range"
              />
            </div>
          </div>

          {chips.length > 0 ? (
            <ul id="admin.charts.ticketSales.selected" className="flex flex-wrap gap-2">
              {chips.map((p) => (
                <li
                  key={p.nightId}
                  id={`admin.charts.ticketSales.selected.${p.nightId}`}
                  className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-1 pl-3 pr-1 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-200"
                >
                  <span>
                    {p.name} · {formatDate(p.startsAt)}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${p.name} ${formatDate(p.startsAt)}`}
                    onClick={() => toggle(p.nightId, false)}
                    className="rounded-full p-1 hover:bg-brand-100 dark:hover:bg-brand-800/50"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div id="admin.charts.ticketSales.nights" className="max-h-56 overflow-y-auto">
            {nights.length === 0 ? (
              <p
                id={nightsLoading ? undefined : 'admin.charts.ticketSales.emptyList'}
                className="text-sm text-gray-500"
              >
                {nightsLoading ? 'Loading…' : 'No night has ticket sales yet.'}
              </p>
            ) : visibleNights.length === 0 ? (
              <p id="admin.charts.ticketSales.noMatch" className="text-sm text-gray-500">
                No nights match
              </p>
            ) : (
              <ul className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                {visibleNights.map((p) => (
                  <li key={p.nightId}>
                    <label className="flex cursor-pointer items-center gap-2 py-1 text-sm text-gray-800 dark:text-gray-100">
                      <input
                        type="checkbox"
                        id={`admin.charts.ticketSales.night.${p.nightId}`}
                        checked={selected.has(p.nightId)}
                        onChange={(event) => toggle(p.nightId, event.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 accent-brand-600"
                      />
                      <span>{p.name}</span>{' '}
                      <span className="text-gray-500 dark:text-gray-400">{formatDate(p.startsAt)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>

      <Card title="Ticket sales">
        {error && !payload ? (
          <div className="p-5">
            <QueryErrorNotice error={error} title="Could not load ticket sales" />
          </div>
        ) : loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : series.length === 0 ? (
          <div id="admin.charts.ticketSales.empty">
            <EmptyState
              title="No ticket sales to show"
              description={
                nightIds.length === 0
                  ? 'Tick at least one night above.'
                  : 'The selected nights have no counted tickets yet.'
              }
            />
          </div>
        ) : (
          <div id="admin.charts.ticketSales.chart">
            <ReactApexChart
              key={chartKey}
              options={options}
              series={series}
              type={kind === 'BAR' ? 'bar' : 'line'}
              height={400}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
